import React, { useContext, useMemo, useState, useEffect } from "react";
import { MyContext } from "../Context/MyContext";
import { FaChevronDown, FaSlidersH } from "react-icons/fa";
import "./FilterSidebar.css";
import { resolveImage } from "../../Utils/imageUtils";

const SECTIONS = [
  { id: "brands", title: "Brands", key: "brand_ids", listKey: "brands", img: true },
  { id: "models", title: "Models", key: "model_ids", dynamic: true },
  { id: "body", title: "Body Type", key: "body_type_ids", listKey: "body_type" },
  { id: "fuel", title: "Fuel", key: "fuel_type_ids", listKey: "fuel_type" },
  { id: "transmission", title: "Transmission", key: "transmission_ids", listKey: "transmission" },
  { id: "seats", title: "Seats", key: "seats_ids", listKey: "seats" },
  { id: "color", title: "Color", key: "color_ids", listKey: "car_color" },
  { id: "features", title: "Features", key: "feature_ids", listKey: "featuers" },
];

export default function FilterSidebar() {
  const {
    brands,
    model,
    fuel_type,
    transmission,
    seats,
    body_type,
    car_color,
    featuers,
    cars,
    filters,
    toggleFilter,
    applyRange,
    clearFilters,
    setCertifiedOnly,
    certifiedCarCount,
    car_trust,
  } = useContext(MyContext);

  const [temp, setTemp] = useState(filters);
  const [openSections, setOpenSections] = useState(() =>
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  useEffect(() => {
    setTemp(filters);
  }, [filters]);

  const filteredModels = useMemo(() => {
    const selectedBrands = filters.brand_ids || [];
    if (selectedBrands.length === 0) return model;
    return model.filter((m) => selectedBrands.includes(m.brand_id));
  }, [filters.brand_ids, model]);

  const activeFilterCount = useMemo(() => {
    let count = filters.certified_only ? 1 : 0;
    Object.entries(filters).forEach(([key, val]) => {
      if (key === "certified_only") return;
      if (Array.isArray(val) && val.length > 0) count += val.length;
    });
    return count;
  }, [filters]);

  const getCount = (key, value) => {
    return cars.filter((car) => {
      for (const filterKey in filters) {
        if (filterKey === key + "s" || filterKey === key + "_ids") continue;
        const selected = filters[filterKey];
        if (Array.isArray(selected) && selected.length > 0) {
          const carKey = filterKey.replace("_ids", "_id");
          if (filterKey === "feature_ids") {
            if (!car.feature_ids?.some((id) => selected.includes(id))) return false;
          } else if (!selected.includes(car[carKey])) {
            return false;
          }
        }
      }
      if (key === "feature_id") {
        return car.feature_ids?.includes(value);
      }
      return car[key] === value;
    }).length;
  };

  const listMap = {
    brands,
    body_type,
    fuel_type,
    transmission,
    seats,
    car_color,
    featuers,
  };

  const getListForSection = (section) => {
    if (section.dynamic) return filteredModels;
    return listMap[section.listKey] || [];
  };

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderItems = (list, key, img = false) => (
    <div className="filter-grid">
      {list.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`filter-chip ${filters[key].includes(item.id) ? "active" : ""}`}
          onClick={() => toggleFilter(key, item.id)}
        >
          {img && item.image && (
            <img src={resolveImage(item.image)} alt="" />
          )}
          <span className="filter-chip-label">{item.name}</span>
          <span className="filter-chip-count">{getCount(key.replace("_ids", "_id"), item.id)}</span>
        </button>
      ))}
    </div>
  );

  const handleChange = (key, value) => {
    setTemp((prev) => ({ ...prev, [key]: Number(value) }));
  };

  const renderRange = (label, minKey, maxKey, minLimit, maxLimit) => {
    const isDisabled =
      temp[minKey] === filters[minKey] && temp[maxKey] === filters[maxKey];

    return (
      <div className="filter-range-group">
        <h4 className="filter-range-label">{label}</h4>
        <div className="filter-range-inputs">
          <input
            type="number"
            min={minLimit}
            max={maxLimit}
            value={temp[minKey]}
            placeholder="Min"
            onChange={(e) => handleChange(minKey, e.target.value)}
          />
          <span className="filter-range-sep">–</span>
          <input
            type="number"
            min={minLimit}
            max={maxLimit}
            value={temp[maxKey]}
            placeholder="Max"
            onChange={(e) => handleChange(maxKey, e.target.value)}
          />
          <button
            type="button"
            className="filter-apply-btn"
            disabled={isDisabled}
            onClick={() => applyRange({ [minKey]: temp[minKey], [maxKey]: temp[maxKey] })}
          >
            Apply
          </button>
        </div>
      </div>
    );
  };

  return (
    <aside className="filter-sidebar">
      <div className="filter-sidebar-head">
        <div className="filter-sidebar-title">
          <FaSlidersH aria-hidden />
          <h3>Filters</h3>
          {activeFilterCount > 0 && (
            <span className="filter-active-badge">{activeFilterCount}</span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button type="button" className="filter-clear-link" onClick={clearFilters}>
            Clear all
          </button>
        )}
      </div>

      <div className="filter-sidebar-body">
        <div className="filter-certified-block">
          <button
            type="button"
            className={`filter-certified-toggle ${filters.certified_only ? "active" : ""}`}
            onClick={() => setCertifiedOnly(!filters.certified_only)}
          >
            <span className="filter-certified-label">
              {car_trust?.badges?.certified || "Certified"} cars only
            </span>
            <span className="filter-certified-count">{certifiedCarCount}</span>
          </button>
        </div>

        {SECTIONS.map((section) => {
          const list = getListForSection(section);
          if (!list.length && section.id !== "models") return null;

          return (
            <div key={section.id} className="filter-section">
              <button
                type="button"
                className="filter-section-toggle"
                onClick={() => toggleSection(section.id)}
                aria-expanded={openSections[section.id]}
              >
                <span>{section.title}</span>
                <FaChevronDown
                  className={`filter-section-chevron ${openSections[section.id] ? "open" : ""}`}
                />
              </button>
              {openSections[section.id] && (
                <div className="filter-section-content">
                  {renderItems(list, section.key, section.img)}
                </div>
              )}
            </div>
          );
        })}

        <div className="filter-ranges-block">
          <h4 className="filter-ranges-heading">Range</h4>
          {renderRange("Model Year", "year_min", "year_max", 2010, 2025)}
          {renderRange("Tire %", "tire_min", "tire_max", 0, 100)}
          {renderRange("Price (₹)", "price_min", "price_max", 0, 10000000)}
          {renderRange("Kilometers", "km_min", "km_max", 0, 1000000)}
        </div>
      </div>

      <button type="button" className="filter-clear-btn" onClick={clearFilters}>
        Clear Filters
      </button>
    </aside>
  );
}
